<script lang="ts" module>
  import type { Component } from 'svelte';
  export interface ChipOption<T> {
    value: T;
    label: string;
    color?: string;
    icon?: Component<any>;
    meta?: string;
    disabled?: boolean;
  }
</script>

<script lang="ts" generics="T extends string | number">
  // A row of chips.
  //   Free-form:   <ChipGroup ariaLabel="Recent"><Chip … /><Chip … /></ChipGroup>
  //   Selectable:  <ChipGroup options={presets} bind:selected onchange={…} />          single choice
  //                <ChipGroup options={days} bind:selected multiple />                   multiple choice
  // `scroll` keeps one line and scrolls horizontally (mobile quick picks).
  import type { Snippet } from 'svelte';
  import Chip from './Chip.svelte';

  interface Props {
    options?: ChipOption<T>[];
    selected?: T[];
    multiple?: boolean;
    /** Single mode: clicking the selected chip clears it. */
    deselectable?: boolean;
    onchange?: (selected: T[]) => void;
    size?: 'sm' | 'md';
    /** Tint chips with their option colour instead of a dot. */
    tint?: boolean;
    scroll?: boolean;
    ariaLabel?: string;
    class?: string;
    children?: Snippet;
  }
  let { options, selected = $bindable([]), multiple = false, deselectable = false, onchange, size = 'md', tint = false, scroll = false, ariaLabel, class: cls = '', children }: Props = $props();

  function toggle(value: T) {
    let next: T[];
    if (multiple) next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
    else next = selected.includes(value) && deselectable ? [] : [value];
    selected = next;
    onchange?.(next);
  }
</script>

<div class="chips {cls}" class:scroll role="group" aria-label={ariaLabel}>
  {#if options}
    {#each options as o (o.value)}
      <Chip
        label={o.label}
        color={o.color}
        icon={o.icon}
        meta={o.meta}
        disabled={o.disabled}
        {size}
        {tint}
        selected={selected.includes(o.value)}
        onclick={() => toggle(o.value)}
      />
    {/each}
  {/if}
  {#if children}{@render children()}{/if}
</div>

<style>
  .chips { display: flex; flex-wrap: wrap; gap: var(--space-2); min-width: 0; }
  .scroll {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: var(--space-0_5);
    -webkit-overflow-scrolling: touch;
  }
  .scroll::-webkit-scrollbar { display: none; }
</style>
